import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { MapContainer, TileLayer, Circle, CircleMarker, useMap, useMapEvents } from "react-leaflet";
import { Crosshair, Loader2, Save, Printer, RefreshCw, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, isAdmin } from "@/lib/auth";
import { LunchFields } from "@/components/LunchFields";

function MapSync({ pos, onPick }) {
  const map = useMap();
  useEffect(() => { map.setView([pos.lat, pos.lng], Math.max(map.getZoom(), 18)); }, [map, pos.lat, pos.lng]);
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

const bestReading = (onBest, onDone) => {
  let best = null;
  const id = navigator.geolocation.watchPosition(({ coords }) => {
    if (!best || coords.accuracy < best.accuracy) { best = coords; onBest(coords); }
  }, () => {}, { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
  setTimeout(() => { navigator.geolocation.clearWatch(id); onDone(best); }, 15000);
};

const printQr = (site) => {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>QR Timbratura - ${site.name}</title></head><body style="font-family:sans-serif;text-align:center;padding:40px">
    <h1>Timbratura presenze</h1><h2>${site.name}</h2><p>Inquadra con la fotocamera del telefono all'entrata e all'uscita</p>
    ${document.getElementById(`punch-qr-${site.id}`).outerHTML}<p style="margin-top:24px">Madonna Store</p></body></html>`);
  w.document.close();
  w.print();
};

function SiteCard({ site, i, admin, onList }) {
  const [f, setF] = useState(site);
  const [acc, setAcc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(site); }, [site]);
  const changed = f.name !== site.name || f.lat !== site.lat || f.lng !== site.lng || Number(f.radius_m) !== site.radius_m
    || (f.lunch_start || null) !== (site.lunch_start || null) || (f.lunch_end || null) !== (site.lunch_end || null);
  const detect = () => {
    if (!navigator.geolocation) return toast.error("Il dispositivo non supporta la geolocalizzazione");
    setBusy(true); setAcc(null);
    bestReading((c) => { setF((p) => ({ ...p, lat: c.latitude, lng: c.longitude })); setAcc(Math.round(c.accuracy)); },
      (best) => { setBusy(false); if (!best) toast.error("Posizione non rilevata: attiva il GPS e riprova"); });
  };
  const call = async (fn, ok) => {
    try { onList((await fn()).data); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setSaving(true);
    await call(() => api.put(`/admin/qr/sites/${site.id}`, { name: f.name.trim(), lat: f.lat, lng: f.lng, radius_m: Number(f.radius_m) || 100, lunch_start: f.lunch_start || null, lunch_end: f.lunch_end || null }), "Sede aggiornata");
    setSaving(false);
  };
  const rotate = () => window.confirm(`Rigenerare il QR di "${site.name}"? Quello stampato smetterà di funzionare.`)
    && call(() => api.post(`/admin/qr/sites/${site.id}/rotate`), "Nuovo QR creato: stampalo e sostituisci quello appeso");
  const remove = () => window.confirm(`Eliminare la sede "${site.name}"? Il suo QR smetterà di funzionare.`)
    && call(() => api.delete(`/admin/qr/sites/${site.id}`), "Sede eliminata");
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-4" data-testid={`qr-site-card-${i}`}>
      {admin ? <Input value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} className="font-semibold" data-testid={`qr-site-name-${i}`} />
        : <p className="font-semibold">{site.name}</p>}
      <div className="text-center"><div className="inline-block rounded-xl border p-2"><QRCodeSVG id={`punch-qr-${site.id}`} value={site.url} size={160} data-testid={`qr-site-qr-${i}`} /></div></div>
      <div className="flex justify-center gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => printQr(site)} data-testid={`qr-site-print-${i}`}><Printer className="h-4 w-4" /> Stampa</Button>
        <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={rotate} data-testid={`qr-site-rotate-${i}`}><RefreshCw className="h-4 w-4" /> Rigenera</Button>
        {admin && !site.main && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={remove} data-testid={`qr-site-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
      <div className="h-48 overflow-hidden rounded-xl border" data-testid={`qr-site-map-${i}`}>
        <MapContainer center={[f.lat, f.lng]} zoom={18} className="h-full w-full">
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
          <Circle center={[f.lat, f.lng]} radius={Number(f.radius_m) || 100} pathOptions={{ color: "#61a229", weight: 1, fillOpacity: 0.12 }} />
          <CircleMarker center={[f.lat, f.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#0f1710", fillOpacity: 1 }} />
          <MapSync pos={f} onPick={(p) => admin && setF((o) => ({ ...o, ...p }))} />
        </MapContainer>
      </div>
      <p className="text-xs text-slate-600" data-testid={`qr-site-coords-${i}`}>{f.lat.toFixed(6)}, {f.lng.toFixed(6)}
        {acc != null && <span className={`ml-2 font-semibold ${acc <= 15 ? "text-[#3d6b16]" : "text-amber-700"}`} data-testid={`qr-site-accuracy-${i}`}>precisione ±{acc} m</span>}</p>
      {!admin && site.lunch_start && <p className="text-xs text-slate-500">Pausa pranzo {site.lunch_start}-{site.lunch_end}</p>}
      {admin && (
        <>
          <div className="flex items-center gap-2 text-sm">Raggio timbratura
            <Input type="number" min="20" max="5000" className="h-9 w-24" value={f.radius_m} onChange={(e) => setF((p) => ({ ...p, radius_m: e.target.value }))} data-testid={`qr-site-radius-${i}`} /> m</div>
          <LunchFields start={f.lunch_start} end={f.lunch_end} onChange={(v) => setF((p) => ({ ...p, ...v }))} testid={`qr-site-lunch-${i}`} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={detect} disabled={busy} className="rounded-full" data-testid={`qr-site-detect-${i}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />} {busy ? "Rilevo…" : "Rileva qui"}</Button>
            <Button onClick={save} disabled={saving || busy || !changed || f.name.trim().length < 2} className="rounded-full" data-testid={`qr-site-save-${i}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva</Button>
          </div>
        </>
      )}
    </div>
  );
}

export function QrSitesPanel() {
  const { user } = useAuth();
  const [sites, setSites] = useState(null);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/admin/qr/sites").then((r) => setSites(r.data)); }, []);
  if (!sites) return null;
  const admin = isAdmin(user);
  const add = async () => {
    if (name.trim().length < 2) return toast.error("Scrivi il nome della sede o del magazzino");
    const m = sites[0];
    try {
      setSites((await api.post("/admin/qr/sites", { name: name.trim(), lat: m.lat, lng: m.lng, radius_m: m.radius_m })).data);
      setName(""); toast.success("Sede creata: vai sul posto e premi \"Rileva qui\" per fissarne la posizione");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="qr-sites-panel">
      <div>
        <p className="font-display text-lg font-semibold">QR Code timbratura</p>
        <p className="text-xs text-slate-500">Un QR per ogni sede o magazzino. Il dipendente può timbrare solo vicino al QR che inquadra.{admin && " Mettiti accanto al QR stampato e premi \"Rileva qui\" (15 secondi, tiene la lettura più precisa) oppure tocca la mappa."}</p>
      </div>
      {sites.map((s, i) => <SiteCard key={s.id} site={s} i={i} admin={admin} onList={setSites} />)}
      {admin && (
        <div className="flex gap-2 rounded-2xl border border-dashed bg-white p-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Magazzino Giugliano" data-testid="qr-site-new-name" />
          <Button onClick={add} className="shrink-0 rounded-full" data-testid="qr-site-add"><Plus className="h-4 w-4" /> Nuova sede</Button>
        </div>
      )}
    </div>
  );
}
